// 고객 상세의 「이 고객의 현장」 목록과 「진행 단계」 스테퍼 — 판정은 journey.ts, 여기는 그리기만.

import { Check, X } from "lucide-react";
import type { CSSProperties } from "react";
import { Link } from "@/lib/nav";

import EmptyState from "@buildpy/ui/empty-state";
import SectionCard from "@buildpy/ui/section-card";
import StatusBadge, { StatusTone } from "@buildpy/ui/status-badge";
import { Button } from "@buildpy/ui/button";
import { EMPTY_MARK } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import ChangeOrderStatusBadge from "@/modules/change-order/ui/status-badge";
import ContractStatusBadge from "@/modules/contract/ui/status-badge";
import { NEW_SALES_SITE, type JourneyStep, type JourneyStepState } from "@buildpy/core/modules/customer/journey";
import type { ChangeOrderNote, CustomerTodo, LinkedDoc } from "@buildpy/core/modules/customer/journey-panel";
import StartQuoteButton from "@/modules/customer/ui/start-quote-button";
import ProjectStatusBadge, { PROJECT_STATUS_TONE } from "@/modules/project/ui/status-badge";
import type { ProjectStatus } from "@buildpy/core/modules/project/types";

export interface SiteRow {
  id: string;
  title: string;
  sub: string;
  status: ProjectStatus;
  pct: number;
  progress: string;
  period: string | null;
  owner: string | null;
}

// 막대 채움 — 클래스는 문자열 그대로 둔다(Tailwind 스캔).
const FILL_CLASS: Record<StatusTone, string> = {
  new: "bg-status-new",
  progress: "bg-status-progress",
  done: "bg-status-done",
  hold: "bg-status-hold",
  dropped: "bg-status-dropped",
};

// 레일(w-52)이 붙는 md 에서는 표 열이 안 들어가 lg 부터 표로 편다 — 고정 폭은 좁은 열에만 둔다.
const COLS = "lg:grid-cols-[minmax(0,1.4fr)_5.5rem_minmax(0,1fr)_minmax(0,9.5rem)_minmax(0,4.5rem)_auto]";

// 현장을 만든 뒤 새로 시작한 영업 한 줄 — 고르면 진행 단계가 그 견적 기준으로 바뀐다.
export interface NewSalesRow {
  step: string;
  doc: string | null;
}

export const CustomerSites = ({
  customerId,
  sites,
  selectedId,
  newSales = null,
}: {
  customerId: string;
  sites: readonly SiteRow[];
  selectedId: string | null;
  newSales?: NewSalesRow | null;
}) => (
  <SectionCard aria-label="이 고객의 현장" className="pb-1.5" data-testid="customer-sites">
    <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <h2 className="text-base font-semibold">
        이 고객의 현장 <span className="text-sm font-normal text-muted-foreground">{sites.length}곳</span>
      </h2>
      <span className="text-xs text-muted-foreground">줄을 고르면 아래 진행 단계가 그 현장 기준으로 바뀝니다</span>
    </div>
    <div className={cn("hidden h-8 items-center gap-x-4 text-xs font-medium text-muted-foreground lg:grid", COLS)}>
      <span>현장</span>
      <span>상태</span>
      <span>공정 진행</span>
      <span>기간</span>
      <span>담당</span>
      <span />
    </div>
    <ul className="-mx-4">
      {newSales && (
        <li
          className={cn(
            "relative flex flex-wrap items-center gap-x-4 gap-y-1 border-l-2 border-t border-l-transparent border-t-border px-4 py-3 hover:bg-muted lg:min-h-12 lg:py-2",
            selectedId === NEW_SALES_SITE && "border-l-primary bg-accent hover:bg-accent"
          )}
          data-testid="customer-new-sales-row">
          <Link
            aria-current={selectedId === NEW_SALES_SITE ? "true" : undefined}
            className="text-sm font-semibold after:absolute after:inset-0"
            href={`/app/customers/${customerId}?site=${NEW_SALES_SITE}`}
            scroll={false}>
            새 영업
          </Link>
          <StatusBadge label={newSales.step} tone="new" />
          {newSales.doc && <span className="min-w-0 truncate text-xs text-muted-foreground">{newSales.doc}</span>}
        </li>
      )}
      {sites.map((site) => {
        const on = site.id === selectedId;
        return (
          <li
            key={site.id}
            className={cn(
              "relative grid grid-cols-1 items-center gap-x-4 gap-y-1.5 border-l-2 border-t border-l-transparent border-t-border px-4 py-3 hover:bg-muted lg:min-h-14 lg:py-2",
              COLS,
              on && "border-l-primary bg-accent hover:bg-accent"
            )}
            data-testid="customer-site-row">
            <span className="flex min-w-0 flex-col gap-y-0.5">
              <Link
                aria-current={on ? "true" : undefined}
                className="truncate text-sm font-semibold after:absolute after:inset-0"
                href={`/app/customers/${customerId}?site=${site.id}`}
                scroll={false}>
                {site.title}
              </Link>
              <span className="truncate text-xs text-muted-foreground">{site.sub}</span>
            </span>
            <span>
              <ProjectStatusBadge status={site.status} />
            </span>
            <span className="flex min-w-0 items-center gap-x-2.5">
              <span aria-hidden className="h-2 min-w-12 max-w-40 flex-1 rounded-full bg-muted">
                <span
                  className={cn("block h-full w-(--w) rounded-full", FILL_CLASS[PROJECT_STATUS_TONE[site.status]])}
                  style={{ "--w": `${site.pct}%` } as CSSProperties}
                />
              </span>
              <span className="truncate text-xs tabular-nums">{site.progress}</span>
            </span>
            <span className="text-xs tabular-nums">{site.period ?? EMPTY_MARK}</span>
            <span className="text-sm">{site.owner ?? EMPTY_MARK}</span>
            <Button asChild className="relative z-10 justify-self-start lg:justify-self-end" size="sm" variant="outline">
              <Link href={`/app/projects/${site.id}`}>현장 보기</Link>
            </Button>
          </li>
        );
      })}
    </ul>
  </SectionCard>
);

const DOT_CLASS: Record<JourneyStepState, string> = {
  done: "bg-status-done text-primary-foreground",
  current: "bg-primary text-primary-foreground ring-4 ring-primary/15",
  todo: "border border-muted-foreground bg-background text-muted-foreground",
  skipped: "border border-dashed border-muted-foreground bg-background text-muted-foreground",
  lost: "bg-status-dropped text-primary-foreground",
};

const LABEL_CLASS: Record<JourneyStepState, string> = {
  done: "font-medium",
  current: "font-semibold text-primary",
  todo: "text-muted-foreground",
  skipped: "text-muted-foreground",
  lost: "font-semibold text-status-dropped",
};

// 이음선 — 지나온 구간은 실선 초록, 건너뛴 단계에 닿으면 점선.
const lineClass = (from: JourneyStep, to: JourneyStep): string => {
  const passed = (from.state === "done" || from.state === "skipped") && to.state !== "todo";
  const dashed = from.state === "skipped" || to.state === "skipped";
  return cn(passed ? "border-status-done" : "border-border", dashed ? "border-dashed" : "border-solid");
};

// 화면 읽기용 상태 — 색·아이콘만으로 알리지 않는다.
export const JOURNEY_STATE_TEXT: Record<JourneyStepState, string> = {
  done: "완료",
  current: "현재 단계",
  todo: "예정",
  skipped: "건너뜀",
  lost: "이탈",
};

export const JourneyStepper = ({
  steps,
  site,
  note,
}: {
  steps: readonly JourneyStep[];
  site: string | null;
  note: ChangeOrderNote | null;
}) => (
  <SectionCard aria-label="진행 단계" data-testid="customer-journey">
    <h2 className="mb-3.5 text-base font-semibold">
      진행 단계{site && <span className="text-sm font-normal text-muted-foreground"> · {site}</span>}
    </h2>
    {/* 폰(md 미만)은 네 칸씩 두 줄로 접어 지금 단계가 가로 스크롤 뒤로 숨지 않게 한다 — md 이상은 한 줄 여덟 칸(좁으면 가로로 민다). */}
    <div className="-mx-4 overflow-x-auto px-4 pb-1">
      <ol className="grid grid-cols-4 gap-y-4 md:min-w-[44rem] md:grid-cols-8 md:gap-y-0">
        {steps.map((step, i) => {
          const next = steps[i + 1];
          const now = step.state === "current" || step.state === "lost";
          const stateText = JOURNEY_STATE_TEXT[step.state];
          // 이탈 단계는 「이탈」 글자를 그리지 않으므로(배지는 aria-hidden) 늘 읽어 준다.
          const spoken = step.state === "lost" || step.when !== stateText;
          return (
            <li
              key={step.key}
              aria-current={now ? "step" : undefined}
              className="relative flex flex-col items-center gap-y-1.5 text-center"
              data-state={step.state}
              data-testid="customer-journey-step">
              {next && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute left-[calc(50%+1rem)] top-3.5 w-[calc(100%-2rem)] border-t-2",
                    i % 4 === 3 && "hidden md:block",
                    lineClass(step, next)
                  )}
                />
              )}
              <span
                aria-hidden
                className={cn(
                  "relative z-10 flex size-7 items-center justify-center rounded-full text-xs font-semibold",
                  DOT_CLASS[step.state]
                )}>
                {step.state === "done" ? (
                  <Check className="size-3.5" strokeWidth={3} />
                ) : step.state === "lost" ? (
                  <X className="size-3.5" strokeWidth={3} />
                ) : (
                  i + 1
                )}
              </span>
              <span className={cn("text-sm", LABEL_CLASS[step.state])}>
                {step.label}
                {spoken && <span className="sr-only"> {stateText}</span>}
              </span>
              {step.state === "lost" && (
                <span aria-hidden>
                  <StatusBadge label="이탈" tone="dropped" />
                </span>
              )}
              {step.when && !(step.state === "lost" && step.when === "이탈") && (
                <span className="text-xs tabular-nums text-muted-foreground">{step.when}</span>
              )}
              {step.doc && (
                <Link className="text-xs text-primary hover:underline" href={step.doc.href}>
                  {step.doc.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </div>
    {note && (
      <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid="customer-journey-change-note">
        <StatusBadge label={`변경공사 ${note.count}건`} tone="progress" />
        <Link className="text-primary hover:underline" href={note.href}>
          {note.text}
        </Link>
      </p>
    )}
  </SectionCard>
);

// 다음 할 일 — 판정은 journey-panel.ts(customerTodos), 여기는 줄과 버튼만.
export const CustomerTodos = ({
  customerId,
  todos,
  className,
}: {
  customerId: string;
  todos: readonly CustomerTodo[];
  className?: string;
}) => (
  <SectionCard aria-label="다음 할 일" className={cn("pb-1.5", className)} data-testid="customer-todos">
    <h2 className="mb-1.5 text-base font-semibold">다음 할 일</h2>
    {todos.length === 0 ? (
      <EmptyState className="pb-3 text-left" variant="plain">
        지금 할 일이 없습니다.
      </EmptyState>
    ) : (
      <ul className="-mx-4">
        {todos.map((todo) => (
          <li
            key={`${todo.tag}-${todo.text}`}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border px-4 py-2.5"
            data-testid="customer-todo">
            <StatusBadge label={todo.tag} tone="progress" />
            <span className="min-w-0 flex-1 text-sm">{todo.text}</span>
            {todo.action?.kind === "link" && (
              <Button asChild size="sm" variant="outline">
                <Link href={todo.action.href}>{todo.action.label}</Link>
              </Button>
            )}
            {todo.action?.kind === "start-quote" && (
              <StartQuoteButton customerId={customerId} label={todo.action.label} variant="outline" />
            )}
          </li>
        ))}
      </ul>
    )}
  </SectionCard>
);

// 연결된 문서 — 계약과 그 변경공사(견적은 「견적」 카드, 현장은 「이 고객의 현장」 에 있다).
export const LinkedDocs = ({ docs, className }: { docs: readonly LinkedDoc[]; className?: string }) => (
  <SectionCard aria-label="연결된 문서" className={cn("pb-1.5", className)} data-testid="customer-docs">
    <h2 className="mb-1.5 text-base font-semibold">연결된 문서</h2>
    <ul className="-mx-4">
      {docs.map((doc) => (
        <li
          key={`${doc.kind}-${doc.id}`}
          className="flex items-center gap-x-2.5 border-t border-border px-4 py-2"
          data-testid="customer-doc">
          <span className="w-14 shrink-0 text-xs text-muted-foreground">{doc.kind === "contract" ? "계약" : "변경공사"}</span>
          <Link className="min-w-0 flex-1 truncate text-sm font-medium hover:underline" href={doc.href}>
            {doc.label}
          </Link>
          {doc.kind === "contract" ? <ContractStatusBadge status={doc.status} /> : <ChangeOrderStatusBadge status={doc.status} />}
          <span className="hidden shrink-0 text-xs tabular-nums text-muted-foreground sm:inline">{doc.date}</span>
        </li>
      ))}
    </ul>
  </SectionCard>
);
